import { useState, FormEvent } from 'react';
import PageLayout from '../../../components/feature/PageLayout';
import PageHero from '../../../components/feature/PageHero';
import AnnouncementBuilder, { BuilderData } from '../../../components/feature/AnnouncementBuilder';
import { submitLocalForm } from '../../../lib/localForms';

export default function FuneralFormPage() {
  const [formData, setFormData] = useState({
    location: '',
    eventDuration: '',
    deceasedFirstName: '',
    deceasedLastName: '',
    dateOfBirth: '',
    dateOfDeath: '',
    placeOfDeath: '',
    fatherFirstName: '',
    fatherLastName: '',
    motherFirstName: '',
    motherLastName: '',
    contactPersonFirstName: '',
    contactPersonLastName: '',
    contactEmail: '',
    contactPhone: '',
    contactAddress: '',
    condolencesLocation: '',
    condolencesDateTime: '',
    funeralLocation: '',
    funeralDateTime: '',
    additionalNotes: '',
    locationCountry: ''
  });

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [builderData, setBuilderData] = useState<BuilderData>({
    photoDataUrl: '',
    customMessage: '',
    imagePosition: 'center',
    themeIndex: 0,
    hasPhoto: false
  });
  const [posterMode, setPosterMode] = useState<'builder' | 'upload'>('builder');
  const [customPosterFile, setCustomPosterFile] = useState<File | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.location || !formData.eventDuration || !formData.deceasedFirstName || 
        !formData.deceasedLastName || !formData.contactEmail || !formData.contactPhone) {
      setSubmitStatus('error');
      setSubmitMessage('Please fill in all required fields.');
      return;
    }

    if (formData.eventDuration === 'two-days') {
      if (!formData.condolencesLocation || !formData.funeralLocation) {
        setSubmitStatus('error');
        setSubmitMessage('Please provide locations for both condolences and funeral events.');
        return;
      }
    }

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      formBody.append('posterOption', posterMode);
      if (posterMode === 'upload') {
        formBody.append('customPoster', customPosterFile ? 'Uncollectable' : 'Not provided');
      } else {
        if (builderData.hasPhoto) formBody.append('announcementPhoto', 'Uncollectable');
        if (builderData.customMessage) formBody.append('announcementMessage', builderData.customMessage);
      }
      const response = await submitLocalForm('Funeral Service Request', formBody);

      if (response.ok) {
        setSubmitStatus('success');
        setSubmitMessage('Your funeral service request has been submitted successfully. Our church office will contact you shortly.');
        setFormData({
          location: '',
          eventDuration: '',
          deceasedFirstName: '',
          deceasedLastName: '',
          dateOfBirth: '',
          dateOfDeath: '',
          placeOfDeath: '',
          fatherFirstName: '',
          fatherLastName: '',
          motherFirstName: '',
          motherLastName: '',
          contactPersonFirstName: '',
          contactPersonLastName: '',
          contactEmail: '',
          contactPhone: '',
          contactAddress: '',
          condolencesLocation: '',
          condolencesDateTime: '',
          funeralLocation: '',
          funeralDateTime: '',
          additionalNotes: '',
          locationCountry: ''
        });
        setBuilderData({
          photoDataUrl: '',
          customMessage: '',
          imagePosition: 'center',
          themeIndex: 0,
          hasPhoto: false
        });
        setCustomPosterFile(null);
        setPosterMode('builder');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitStatus('error');
        setSubmitMessage('Failed to submit the form. Please try again.');
      }
    } catch {
      setSubmitStatus('error');
      setSubmitMessage('An error occurred. Please try again later.');
    }
  };

  return (
    <PageLayout>
      <PageHero 
        title="Funeral Service Request"
        subtitle="May their memory be eternal"
      />
      
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Biblical Verse */}
        <div className="bg-burgundy-50 border-l-4 border-burgundy-700 p-6 mb-12 rounded-r-lg">
          <p className="text-lg text-gray-800 font-serif italic mb-2">
            "Blessed are those who mourn, for they shall be comforted."
          </p>
          <p className="text-sm text-gray-600">— Matthew 5:4</p>
        </div>

        <form id="funeral-form" data-local-form onSubmit={handleSubmit} className="space-y-10">
          {/* Location Selection */}
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-map-pin-line text-gold-600"></i>
              Service Location
            </h2>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Where is the reposed? <span className="text-red-500">*</span>
              </label>
              <select
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
              >
                <option value="">Select location</option>
                <option value="here">Here (Local)</option>
                <option value="egypt">Egypt</option>
                <option value="other-country">Another Country</option>
              </select>
              {formData.location === 'other-country' && (
                <input
                  type="text"
                  name="locationCountry"
                  value={formData.locationCountry}
                  onChange={handleInputChange}
                  placeholder="Please specify the country"
                  className="mt-3 w-full px-4 py-3 border border-gold-400 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                />
              )}
            </div>
          </div>

          {/* Event Duration Selection */}
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-calendar-event-line text-gold-600"></i>
              Event Duration
            </h2>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Service Duration <span className="text-red-500">*</span>
              </label>
              <select
                name="eventDuration"
                value={formData.eventDuration}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
              >
                <option value="">Select duration</option>
                <option value="one-day">One Day (Funeral Service)</option>
                <option value="two-days">Two Days (Condolences + Funeral)</option>
              </select>
            </div>

            {formData.eventDuration === 'two-days' && (
              <div className="mt-6 space-y-6 pt-6 border-t border-gray-200">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Condolences Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="condolencesLocation"
                      value={formData.condolencesLocation}
                      onChange={handleInputChange}
                      required={formData.eventDuration === 'two-days'}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                      placeholder="Enter location"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preferred Condolences Date &amp; Time
                    </label>
                    <input
                      type="datetime-local"
                      name="condolencesDateTime"
                      value={formData.condolencesDateTime}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Funeral Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="funeralLocation"
                      value={formData.funeralLocation}
                      onChange={handleInputChange}
                      required={formData.eventDuration === 'two-days'}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                      placeholder="Enter location"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preferred Funeral Date &amp; Time
                    </label>
                    <input
                      type="datetime-local"
                      name="funeralDateTime"
                      value={formData.funeralDateTime}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                <div className="bg-burgundy-50 border border-burgundy-200 rounded-lg p-4 flex items-start gap-3">
                  <i className="ri-information-line text-burgundy-700 text-lg flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-burgundy-800"><strong>Please note:</strong> Nothing is confirmed until Father Anthony has approved the requested date and time, unless it has already been confirmed beforehand. The church office will contact you once your request has been reviewed.</p>
                </div>
              </div>
            )}

            {formData.eventDuration === 'one-day' && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Funeral Location
                    </label>
                    <input
                      type="text"
                      name="funeralLocation"
                      value={formData.funeralLocation}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                      placeholder="Enter location"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preferred Funeral Date &amp; Time
                    </label>
                    <input
                      type="datetime-local"
                      name="funeralDateTime"
                      value={formData.funeralDateTime}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                <div className="mt-4 bg-burgundy-50 border border-burgundy-200 rounded-lg p-4 flex items-start gap-3">
                  <i className="ri-information-line text-burgundy-700 text-lg flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-burgundy-800"><strong>Please note:</strong> Nothing is confirmed until Father Anthony has approved the requested date and time, unless it has already been confirmed beforehand. The church office will contact you once your request has been reviewed.</p>
                </div>
              </div>
            )}
          </div>

          {/* Deceased Information */}
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-user-line text-gold-600"></i>
              Deceased Information
            </h2>
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="deceasedFirstName"
                    value={formData.deceasedFirstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="deceasedLastName"
                    value={formData.deceasedLastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth</label>
                  <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Death</label>
                  <input type="date" name="dateOfDeath" value={formData.dateOfDeath} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Place of Death</label>
                <input type="text" name="placeOfDeath" value={formData.placeOfDeath} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" placeholder="City, Province, Country" />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Father's First Name</label>
                  <input type="text" name="fatherFirstName" value={formData.fatherFirstName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" placeholder="Enter first name" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Father's Last Name</label>
                  <input type="text" name="fatherLastName" value={formData.fatherLastName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" placeholder="Enter last name" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mother's First Name</label>
                  <input type="text" name="motherFirstName" value={formData.motherFirstName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" placeholder="Enter first name" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Mother's Last Name</label>
                  <input type="text" name="motherLastName" value={formData.motherLastName} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" placeholder="Enter last name" />
                </div>
              </div>
            </div>
          </div>

          {/* Family Contact Information */}
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-contacts-line text-gold-600"></i>
              Family Contact Information
            </h2>
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Person First Name <span className="text-red-500">*</span></label>
                  <input type="text" name="contactPersonFirstName" value={formData.contactPersonFirstName} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" placeholder="Enter first name" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Person Last Name <span className="text-red-500">*</span></label>
                  <input type="text" name="contactPersonLastName" value={formData.contactPersonLastName} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" placeholder="Enter last name" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address <span className="text-red-500">*</span></label>
                  <input type="email" name="email" value={formData.contactEmail} onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" placeholder="contact@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number <span className="text-red-500">*</span></label>
                  <input type="tel" name="contactPhone" value={formData.contactPhone} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" placeholder="(123) 456-7890" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Address</label>
                <input type="text" name="contactAddress" value={formData.contactAddress} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm" placeholder="Street Address, City, Province, Postal Code" />
              </div>
            </div>
          </div>

          {/* Announcement Poster */}
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
              <i className="ri-image-line text-gold-600 mr-3"></i>Memorial Announcement Poster
            </h2>
            <p className="text-sm text-gray-500 mb-6">Choose how you'd like to create the memorial announcement.</p>

            {/* Mode Toggle */}
            <div className="flex gap-4 mb-8">
              <button
                type="button"
                onClick={() => setPosterMode('builder')}
                className={`flex-1 flex items-center justify-center gap-2 px-5 py-4 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${posterMode === 'builder' ? 'border-burgundy-700 bg-burgundy-50 text-burgundy-700 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                <i className="ri-magic-line text-lg"></i>
                <span>Use Announcement Builder</span>
                {posterMode === 'builder' && <i className="ri-check-line text-burgundy-700 ml-1"></i>}
              </button>
              <button
                type="button"
                onClick={() => setPosterMode('upload')}
                className={`flex-1 flex items-center justify-center gap-2 px-5 py-4 rounded-lg border-2 transition-all cursor-pointer whitespace-nowrap ${posterMode === 'upload' ? 'border-burgundy-700 bg-burgundy-50 text-burgundy-700 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                <i className="ri-upload-cloud-line text-lg"></i>
                <span>Upload My Own Poster</span>
                {posterMode === 'upload' && <i className="ri-check-line text-burgundy-700 ml-1"></i>}
              </button>
            </div>

            {/* Upload Mode */}
            {posterMode === 'upload' && (
              <div>
                <p className="text-sm text-gray-600 mb-4">Upload your own custom-designed memorial poster. Accepted formats: JPG, PNG, PDF.</p>
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gold-500 hover:bg-gold-50 transition-colors">
                  {customPosterFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <i className="ri-file-check-line text-4xl text-gold-600"></i>
                      <span className="text-sm font-medium text-burgundy-700">{customPosterFile.name}</span>
                      <span className="text-xs text-gray-500">Click to replace</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-gray-500">
                      <i className="ri-upload-cloud-2-line text-4xl"></i>
                      <span className="text-sm font-medium">Click to upload your poster</span>
                      <span className="text-xs">JPG, PNG, PDF up to 10MB</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                    onChange={(e) => setCustomPosterFile(e.target.files?.[0] ?? null)}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Announcement Builder (shown only in builder mode) */}
          {posterMode === 'builder' && (
            <AnnouncementBuilder
              type="funeral"
              name1={formData.deceasedFirstName}
              familyName1={formData.deceasedLastName}
              birthDate={formData.dateOfBirth}
              deathDate={formData.dateOfDeath}
              eventDate={formData.funeralDateTime ? formData.funeralDateTime.split('T')[0] : ''}
              eventLocation={formData.funeralLocation}
              onChange={setBuilderData}
            />
          )}

          {/* Additional Notes */}
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <i className="ri-file-text-line text-gold-600"></i>
              Additional Notes
            </h2>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Special Requests or Information
              </label>
              <textarea
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleInputChange}
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm resize-none"
                placeholder="Please share any additional information or special requests (max 500 characters)"
              />
              <p className="text-xs text-gray-500 mt-2">
                {formData.additionalNotes.length}/500 characters
              </p>
            </div>
          </div>

          {/* Submit Status Messages */}
          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg">
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-xl text-green-600 mt-0.5"></i>
                <p className="text-sm">{submitMessage}</p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line text-xl text-red-600 mt-0.5"></i>
                <p className="text-sm">{submitMessage}</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-center pt-6">
            <button
              type="submit"
              className="bg-burgundy-700 hover:bg-burgundy-800 text-white px-12 py-4 rounded-lg font-semibold text-base transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Submit Funeral Service Request
            </button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
}
